import React, { useMemo } from 'react';

import { motion } from 'framer-motion';

import { interactiveHoverVariants } from '@/animations';
import { Icon } from '@/utils/icons';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
  const renderedElements = useMemo(() => {
    const pages: (number | string)[] = [];
    const delta = 2;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        pages.push(i);
      } else if (i === currentPage - delta - 1 || i === currentPage + delta + 1) {
        pages.push('...');
      }
    }

    const filtered = pages.filter((v, i, a) => v !== '...' || a[i - 1] !== '...');
    return filtered.map((page, idx) => {
      if (page === '...') {
        const isLeft = idx < filtered.length / 2;
        return {
          key: isLeft ? 'ellipsis-left' : 'ellipsis-right',
          value: page,
        };
      }
      return {
        key: `page-${page}`,
        value: page,
      };
    });
  }, [currentPage, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <div className="mt-6 flex justify-center">
      <nav
        aria-label="Pagination"
        className="flex items-center gap-2 rounded-2xl border border-neutral-100 bg-neutral-50/50 p-2 shadow-sm backdrop-blur-sm dark:border-neutral-800 dark:bg-neutral-900">
        <motion.button
          variants={interactiveHoverVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="hover:text-primary-600 flex h-10 w-10 items-center justify-center rounded-xl text-neutral-400 transition-all hover:bg-white disabled:cursor-not-allowed disabled:opacity-30 dark:text-neutral-500 dark:hover:bg-neutral-800"
          aria-label="Previous page">
          <Icon name="ChevronLeft" size={20} />
        </motion.button>

        <div className="mx-1 flex items-center gap-1">
          {renderedElements.map(item =>
            typeof item.value === 'number' ? (
              <motion.button
                key={item.key}
                variants={interactiveHoverVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => onPageChange(item.value as number)}
                aria-current={item.value === currentPage ? 'page' : undefined}
                aria-label={`Page ${item.value}`}
                className={`font-display flex h-10 w-10 items-center justify-center rounded-xl text-sm font-bold transition-all ${
                  item.value === currentPage
                    ? 'bg-primary-500 shadow-primary-100 scale-110 text-white shadow-lg'
                    : 'hover:text-primary-600 text-neutral-500 hover:bg-white'
                }`}>
                {item.value}
              </motion.button>
            ) : (
              <span key={item.key} className="px-1 text-neutral-400 select-none">
                ...
              </span>
            )
          )}
        </div>

        <motion.button
          variants={interactiveHoverVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="hover:text-primary-600 flex h-10 w-10 items-center justify-center rounded-xl text-neutral-400 transition-all hover:bg-white disabled:cursor-not-allowed disabled:opacity-30 dark:text-neutral-500 dark:hover:bg-neutral-800"
          aria-label="Next page">
          <Icon name="ChevronRight" size={20} />
        </motion.button>
      </nav>
    </div>
  );
};

export default React.memo(Pagination);
